import { RefObject, useEffect } from "react";

interface ParallaxTransformOptions {
  /** 0..1 where 0.3 = 30% of scroll distance */
  speed?: number;
  direction?: "up" | "down";
  /** Disable effect entirely (e.g. when no background is present) */
  disabled?: boolean;
  /** Clamp absolute translateY to avoid extreme values */
  maxOffset?: number;
}

/**
 * Smooth parallax that does NOT trigger React re-renders.
 * It updates the element transform imperatively inside requestAnimationFrame.
 */
export const useParallaxTransform = (
  ref: RefObject<HTMLElement>,
  options: ParallaxTransformOptions = {}
) => {
  const {
    speed = 0.4,
    direction = "down",
    disabled = false,
    maxOffset = 180,
  } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el || disabled) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    let rafId = 0;
    let running = false;
    let lastKnownScrollY = window.scrollY;
    let lastScrollChangeAt = performance.now();

    // Smoothed movement (helps eliminate perceived stutter on some devices)
    let current = 0;
    let target = 0;
    const ease = 0.12;

    const clamp = (value: number, min: number, max: number) =>
      Math.min(max, Math.max(min, value));

    const computeTarget = () => {
      const raw = lastKnownScrollY * speed;
      const signed = direction === "up" ? -raw : raw;
      target = clamp(signed, -maxOffset, maxOffset);
    };

    const tick = (now: number) => {
      // Track scroll position every frame to keep momentum scrolling smooth
      const y = window.scrollY;
      if (y !== lastKnownScrollY) {
        lastKnownScrollY = y;
        lastScrollChangeAt = now;
      }

      computeTarget();

      // Ease towards target for smoother perceived motion
      current += (target - current) * ease;

      // Snap & stop when close enough
      if (Math.abs(target - current) < 0.1) {
        current = target;
      }

      // translate3d keeps it on the compositor for smoother scrolling
      el.style.transform = `translate3d(0, ${current.toFixed(2)}px, 0)`;

      const idleFor = now - lastScrollChangeAt;
      const settled = Math.abs(target - current) < 0.1;

      // Stop the loop shortly after scrolling stops + we're visually settled
      if (idleFor > 140 && settled) {
        running = false;
        rafId = 0;
        return;
      }

      rafId = window.requestAnimationFrame(tick);
    };

    const onScroll = () => {
      lastKnownScrollY = window.scrollY;
      lastScrollChangeAt = performance.now();
      if (running) return;
      running = true;
      rafId = window.requestAnimationFrame(tick);
    };

    // One-time compositor hints
    el.style.willChange = "transform";
    // Helps some GPUs avoid flicker during transforms
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (el.style as any).backfaceVisibility = "hidden";

    // Initial paint
    computeTarget();
    current = target;
    el.style.transform = `translate3d(0, ${current.toFixed(2)}px, 0)`;

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [ref, speed, direction, disabled, maxOffset]);
};
